import { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { Users, UserPlus, ShieldCheck, Trophy, Star, CalendarCheck, Award } from "lucide-react";

const GOLD = "#C79A3B", CHARCOAL = "#101820", COPPER = "#C76A32", GREEN = "#2E7D32", CHAMP = "#E4C675";
const PIE = [GOLD, CHARCOAL, COPPER, GREEN, CHAMP, "#8a6a1e"];

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-lg bg-[#101820] grid place-items-center"><Icon className="h-5 w-5 text-[#C79A3B]" /></div>
        <span className="text-3xl font-heading font-extrabold text-[#101820]">{value}</span>
      </div>
      <div className="mt-3 text-sm text-muted-foreground">{label}</div>
    </div>
  );
}
function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
      <h3 className="font-heading font-semibold text-[#101820] mb-4">{title}</h3>
      <div style={{ width: "100%", height: 240 }}>{children}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <AdminLayout title="Dashboard"><div className="text-muted-foreground">Loading…</div></AdminLayout>;
  const m = d.metrics;

  return (
    <AdminLayout title="Recruitment Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Total Applicants" value={m.total_applicants} testid="kpi-total-applicants" />
        <Kpi icon={UserPlus} label="New (7 days)" value={m.new_applications} testid="kpi-new-applications" />
        <Kpi icon={ShieldCheck} label="Eligible Applicants" value={m.eligible_applicants} testid="kpi-eligible" />
        <Kpi icon={Trophy} label="Top Matches (80+)" value={m.top_matches} testid="kpi-top-matches" />
        <Kpi icon={Star} label="Shortlisted" value={m.shortlisted} testid="kpi-shortlisted" />
        <Kpi icon={CalendarCheck} label="Interviews" value={m.interviews} testid="kpi-interviews" />
        <Kpi icon={Award} label="Offers" value={m.offers} testid="kpi-offers" />
        <div className="bg-[#101820] rounded-lg shadow-sm p-5 text-[#FAF6EE]">
          <div className="text-[#C79A3B] text-xs uppercase tracking-wider">Avg Fit Score</div>
          <div className="text-3xl font-heading font-extrabold mt-2">{d.role_card.avg_fit_score}<span className="text-lg text-[#C79A3B]">/100</span></div>
          <div className="text-xs text-[#FAF6EE]/60 mt-1">Eligibility rate {d.role_card.eligibility_rate}%</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#C79A3B] font-semibold">Current Role</div>
          <h2 className="font-heading text-xl font-bold text-[#101820]">{d.role_card.title}</h2>
        </div>
        <div className="flex gap-6 text-center">
          {[["Applications", d.role_card.applications],["Eligibility", `${d.role_card.eligibility_rate}%`],["Avg Fit", d.role_card.avg_fit_score],["Shortlisted", d.role_card.shortlisted],["Interviewed", d.role_card.interviewed]].map(([l, v]) => (
            <div key={l}><div className="text-2xl font-heading font-bold text-[#101820]">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Applicants by Week">
          <ResponsiveContainer><LineChart data={d.charts.by_week}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="name" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Line type="monotone" dataKey="value" stroke={GOLD} strokeWidth={2} dot={{ fill: CHARCOAL }} /></LineChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Applicants by Status">
          <ResponsiveContainer><BarChart data={d.charts.by_status}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="name" fontSize={10} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Bar dataKey="value" fill={GOLD} radius={[4,4,0,0]} /></BarChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Applicants by County">
          <ResponsiveContainer><BarChart data={d.charts.by_county} layout="vertical"><XAxis type="number" allowDecimals={false} fontSize={11} /><YAxis type="category" dataKey="name" fontSize={11} width={70} /><Tooltip /><Bar dataKey="value" fill={CHARCOAL} radius={[0,4,4,0]} /></BarChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Applicants by Experience Band">
          <ResponsiveContainer><PieChart><Pie data={d.charts.by_experience} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>{d.charts.by_experience.map((e, i) => <Cell key={e.name || i} fill={PIE[i % PIE.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>
        </ChartCard>
      </div>
    </AdminLayout>
  );
}
